import { CUSTOM_EVENTS } from './custom_events'

// Fail-closed PII scrub (GRC-493), installed as posthog's `before_send`. Every event name and
// property that leaves the browser is one a human named below; an unrecognized property is
// alarmed and dropped, and if the scrub itself cannot run the whole event is dropped. The
// session-replay tape ($snapshot_data) is kept whole by design — replays are protected by access
// control (scoped keys, no MCP).
const SCRUB_VERSION = 1

// Report an unrecognized PostHog payload to Honeybadger so a format change is visible. We send
// the event name and the offending property NAMES only — never property VALUES, which may hold
// PII. Honeybadger is loaded globally (layouts/_honeybadger) and is absent in development/test,
// so guard on its presence. Fire each distinct problem once.
const alarm = (reason, context) => {
  try {
    if (typeof window === 'undefined' || !window.Honeybadger) return
    const details = Object.assign({ reason, scrub_version: SCRUB_VERSION }, context || {})
    const key =
      reason + ':' + ((context && context.event) || '') + ':' + JSON.stringify((context && context.properties) || '')
    const fired = (window.__posthogScrubAlarms = window.__posthogScrubAlarms || Object.create(null))
    if (fired[key]) return
    fired[key] = true
    window.Honeybadger.notify('PostHog scrub dropped an event: ' + reason, {
      name: 'PostHogScrubDropped',
      context: details
    })
  } catch {
    // the alarm must never break the scrub
  }
}

// Property groups shared across events, composed into ALLOWED below.
const COMMON = [
  'token',
  'distinct_id',
  '$device_id',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$time',
  '$lib',
  '$lib_version',
  '$lib_rate_limit_remaining_tokens',
  '$is_identified',
  '$process_person_profile',
  '$config_defaults',
  '$os',
  '$os_version',
  '$browser',
  '$browser_version',
  '$browser_language',
  '$browser_language_prefix',
  '$device_type',
  '$timezone',
  '$timezone_offset',
  '$raw_user_agent',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$host',
  '$referring_domain',
  '$session_entry_host',
  '$session_entry_referring_domain',
  '$pageview_id',
  'person_id',
  'impersonator_id',
  'impersonator_email',
  '$current_url',
  '$pathname',
  '$referrer',
  '$session_entry_url',
  '$session_entry_pathname',
  '$user_id',
  '$session_entry_referrer'
]
const PREV_PAGEVIEW = [
  '$prev_pageview_id',
  '$prev_pageview_duration',
  '$prev_pageview_last_scroll',
  '$prev_pageview_last_scroll_percentage',
  '$prev_pageview_max_scroll',
  '$prev_pageview_max_scroll_percentage',
  '$prev_pageview_last_content',
  '$prev_pageview_last_content_percentage',
  '$prev_pageview_max_content',
  '$prev_pageview_max_content_percentage',
  '$prev_pageview_pathname'
]
const ELEMENTS = ['$ce_version', '$event_type', '$elements_chain']
// Session-replay tape. $snapshot_data is kept whole — replays are protected by access control,
// not masking — so it is just an allowlisted key; we never dig into the array.
const SNAPSHOT = ['$snapshot_bytes', '$snapshot_data', '$snapshot_host']
// SDK telemetry, remote-config echoes and feature-flag state the SDK attaches to every event.
// Known and unwanted: dropped silently, never sent. The `$sdk_debug_` family grows with every SDK
// release and never carries app data, so it is matched by prefix rather than enumerated.
const SDK_INTERNAL = [
  '$sdk_dist_channel',
  '$initialization_time',
  '$configured_session_timeout_ms',
  '$autocapture_disabled_server_side',
  '$web_vitals_enabled_server_side',
  '$web_vitals_allowed_metrics',
  '$exception_capture_enabled_server_side',
  '$dead_clicks_enabled_server_side',
  '$debug_first_full_snapshot_timestamp',
  '$session_recording_start_reason',
  '$recording_status',
  '$snapshot_max_depth_exceeded',
  '$active_feature_flags',
  '$feature_flag_payloads',
  '$feature_flag_request_id'
]
const isSdkDebug = key => key.startsWith('$sdk_debug_')

// Every event we send. Key present ⇒ event allowed. `allow` is forwarded; `disallow` is dropped
// silently. A property in neither list is unrecognized — alarm but still send.
// Page titles and element text can hold client names; $elements_chain already carries the text.
const ALLOWED = {
  $snapshot: { allow: COMMON.concat(SNAPSHOT), disallow: SDK_INTERNAL },
  $pageview: { allow: COMMON.concat(PREV_PAGEVIEW), disallow: SDK_INTERNAL.concat(['title']) },
  $pageleave: { allow: COMMON.concat(PREV_PAGEVIEW), disallow: SDK_INTERNAL },
  $autocapture: { allow: COMMON.concat(ELEMENTS), disallow: SDK_INTERNAL.concat(['$el_text']) },
  $rageclick: { allow: COMMON.concat(ELEMENTS), disallow: SDK_INTERNAL.concat(['$el_text']) },
  $set: { allow: COMMON.concat(['$set', '$set_once']), disallow: SDK_INTERNAL }
}
Object.entries(CUSTOM_EVENTS).forEach(([name, keys]) => {
  ALLOWED[name] = { allow: COMMON.concat(keys), disallow: SDK_INTERNAL }
})

// Events we knowingly don't care about: drop silently, no alarm, no property inspection.
const IGNORED = ['$identify', 'session_summary_ready']

// $set / $set_once carry a nested bucket of person properties. The same fail-closed rule applies
// to their keys — only key NAMES are ever inspected, never the values (PII).
const BUCKETS = ['$set', '$set_once']
const PERSON = { allow: ['email'], disallow: [] }

export const beforeSend = event => {
  try {
    if (!event || typeof event.event !== 'string') return null
    if (IGNORED.includes(event.event)) return null

    const config = ALLOWED[event.event]
    if (!config) {
      alarm('event_not_allowlisted', { event: event.event })
      return null
    }

    // Alarm on anything unrecognized, but still send the event — props is built from
    // config.allow only, so the unexpected keys are dropped and never leave the browser.
    const p = event.properties || {}
    const unexpected = Object.keys(p).filter(
      key => !config.allow.includes(key) && !config.disallow.includes(key) && !isSdkDebug(key)
    )
    if (unexpected.length > 0) {
      alarm('property_not_allowlisted', { event: event.event, properties: unexpected })
    }

    const props = {}
    config.allow.forEach(key => {
      if (p[key] !== undefined) props[key] = p[key]
    })

    // Same rule one level deeper: alarm on any unrecognized key inside a $set / $set_once
    // bucket, then rebuild the bucket from PERSON.allow only so that key can't leak through.
    BUCKETS.forEach(bucketName => {
      const bucket = props[bucketName]
      if (bucket === undefined) return
      const isObject = bucket && typeof bucket === 'object'
      const badKeys = isObject
        ? Object.keys(bucket).filter(key => !PERSON.allow.includes(key) && !PERSON.disallow.includes(key))
        : ['<invalid>']
      if (badKeys.length === 0) return

      alarm('person_property_not_allowlisted', { event: event.event, bucket: bucketName, properties: badKeys })
      const cleanBucket = {}
      PERSON.allow.forEach(key => {
        if (isObject && bucket[key] !== undefined) cleanBucket[key] = bucket[key]
      })
      props[bucketName] = cleanBucket
    })

    const clean = { uuid: event.uuid, event: event.event, properties: props }
    if (event.timestamp !== undefined) clean.timestamp = event.timestamp
    return clean
  } catch {
    alarm('scrub_error')
    return null
  }
}
